import { useClipboard } from "client/hooks/utils/useClipboard";
import { capitalize } from "client/utils/capitalize";
import { translateFunc } from "client/utils/translateFunc";
import { CopyIcon } from "components/common/Icons/CopyIcon";
import { TickIcon } from "components/common/Icons/TickIcon";
import { useToast } from "components/common/Toast/useToast";
import tinycolor from "tinycolor2";

interface ChipProps extends RGB {
    i: number;
}

export const Chip = ({ r, g, b, i }: ChipProps) => {
    const tiny = tinycolor({ r, g, b });
    const hex = tiny.toHex();
    const value = `#${hex}`;
    const isDark = tiny.isDark();
    const { handleCopy, isCopied } = useClipboard();
    const { toast } = useToast();

    const copyText = capitalize(`${translateFunc("copied")}`);

    function handleClick() {
        handleCopy(value);
        toast({
            position: "top-right",
            text: `${copyText}!`,
            status: "success",
            time: 3000,
        });
    }

    return (
        <div
            className={`group 
                flex items-center justify-between lg:justify-center relative cursor-pointer w-full h-12 
                first:rounded-t-md last:rounded-b-md 
                lg:first:rounded-l-md lg:first:rounded-tr-none lg:last:rounded-bl-none lg:last:rounded-r-md
            `}
            style={{ backgroundColor: `rgb(${r},${g},${b})` }}
            onClick={handleClick}
        >
            <div className="hidden lg:flex justify-center absolute left-0 right-0 bottom-full mb-4">
                <div className="text-xs px-1 rounded-r-md rounded-l-md bg-black dark:bg-white text-white dark:text-black">
                    {i * 10}%
                </div>
            </div>
            <div className="flex items-center lg:hidden justify-center ml-4">
                <div
                    className={`text-xs px-1 rounded-r-md rounded-l-md bg-black dark:bg-white text-white dark:text-black`}
                >
                    {i * 10}%
                </div>
            </div>
            <div
                className={`lg:absolute w-full h-full flex items-center justify-center top-full left-0 right-0 lg:mt-2 lg:hidden lg:group-hover:block ${
                    isDark
                        ? "text-white lg:text-black lg:dark:text-white"
                        : "text-black lg:text-black lg:dark:text-white"
                }`}
            >
                {value}
            </div>
            <div className="mr-4 lg:mr-0 cursor-pointer transition-transform duration-300 ease-in-out">
                {isCopied ? (
                    <TickIcon width={20} height={20} fill={isDark ? "white" : "black"} />
                ) : (
                    <CopyIcon fill={isDark ? "white" : "black"} width={20} height={20} />
                )}
            </div>
        </div>
    );
};

type RGB = {
    r: number;
    g: number;
    b: number;
};

export function rgbShade(rgb: RGB, i: number) {
    return {
        r: rgb.r * (1 - 0.1 * i),
        g: rgb.g * (1 - 0.1 * i),
        b: rgb.b * (1 - 0.1 * i),
    };
}

export function rgbTint(rgb: RGB, i: number) {
    return {
        r: rgb.r + (255 - rgb.r) * i * 0.1,
        g: rgb.g + (255 - rgb.g) * i * 0.1,
        b: rgb.b + (255 - rgb.b) * i * 0.1,
    };
}
